import { useQuery } from '@tanstack/react-query'
import { fromUnixTime, milliseconds, differenceInDays } from 'date-fns'
import {
  ProfileDetails,
  fetchProfileDetails,
} from '@components/profile/useProfileDetails'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { DAOS_REFS, REFERRER_KEY } from 'utils/constants'
import { useMemo } from 'react'
import { usePlausibleChecked } from '../../hooks/usePlausibleChecked'

export const calculateFee = (daysSinceStart: number): number => {
  if (daysSinceStart <= 30) return 99
  if (daysSinceStart <= 60) return 50
  return 25
}

export const getNextFeeRate = (currentFee: number): number => {
  if (currentFee === 99) return 50
  if (currentFee === 50) return 25
  return 25
}

export function useReferrerFee() {
  const [localReferrer] = useLocalStorage(REFERRER_KEY)
  const plausible = usePlausibleChecked()
  const daoPartner = useMemo(
    () =>
      (localReferrer?.wallet &&
        DAOS_REFS.find((x) => x.wallet === localReferrer.wallet)) ||
      undefined,
    [localReferrer?.wallet],
  )
  const referrerWalletAddress = localReferrer?.wallet || ''

  const { data: referrerProfileDetails, isInitialLoading } =
    useQuery<ProfileDetails | null>({
      queryKey: ['getReferrerProfileDetails', referrerWalletAddress],
      queryFn: async () => {
        if (!referrerWalletAddress) {
          throw new Error('Partner wallet address is not available')
        }

        const profileDetails = await fetchProfileDetails(referrerWalletAddress)

        if (!profileDetails) return null
        plausible('Refferer', {
          props: {
            refererWallet: profileDetails.wallet,
            referralName: profileDetails.username,
            partnerStartAtUnixTime: profileDetails.partnerStartAtUnixTime || 0,
          },
        })
        const { partnerStartAtUnixTime, ...otherProps } = profileDetails
        return {
          ...otherProps,
          partnerStartAt:
            partnerStartAtUnixTime != null
              ? fromUnixTime(partnerStartAtUnixTime)
              : null,
        }
      },
      enabled: !!referrerWalletAddress && !daoPartner,
      staleTime: milliseconds({ minutes: 10 }),
      retry: 3,
    })

  const referrerFee = useMemo(() => {
    const startDate = daoPartner
      ? fromUnixTime(daoPartner.partnerStartAtUnixTime)
      : referrerProfileDetails?.partnerStartAt

    if (!startDate) return 0

    const daysSinceStart = differenceInDays(new Date(), startDate)

    return calculateFee(daysSinceStart)
  }, [referrerProfileDetails?.partnerStartAt, daoPartner])

  return {
    referrerFee,
    loading: isInitialLoading,
  }
}
